import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth, useRole, useStaffPermissions } from '@structural-climbing/auth'
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Chip,
  Spinner,
  Tabs,
  Tab,
  useInfiniteScroll,
} from '@structural-climbing/ui'
import type { SubscriptionStatus } from '@structural-climbing/ui'
import { useGymThemeContext } from '../../layouts/useGymTheme'
import { MemberAvatar } from './MemberAvatar'
import { MemberName } from './MemberName'
import { SubBadge } from './SubBadge'
import { formatDuration } from './formatDuration'
import {
  CurrentSubscriptionCard,
  PreviousSubscriptionRow,
  type ProfileSubscriptionData,
} from './ProfileSubscription'
import { ChildProfileModal } from './ChildProfileModal'
import { AddonsSection } from './AddonsSection'
import { TrainerAssignmentsSection } from '../trainers/TrainerAssignmentsSection'
import { SignedDocumentsTab } from './SignedDocumentsTab'
import { ClubMemberStar } from './ClubMemberStar'
import { ProfileDetails, formatDetailDate, usePersonDetailItems } from './ProfileDetails'
import { EditMemberDetailsModal } from './EditMemberDetailsModal'
import { FreezeSubscriptionModal } from './FreezeSubscriptionModal'
import { RenewalSection } from './RenewalSection'
import { AddMemberModal } from './AddMemberModal'
import { GymDialog } from '../../components/GymDialog'

interface MemberDetails {
  userId: string
  name: string
  firstName?: string | null
  lastName?: string | null
  email: string
  /** When the member proved the address by code; null if they have not. */
  emailVerifiedAt?: string | null
  /** The parent or guardian who signed for a member who joined as a minor. */
  guardian?: {
    name: string
    firstName?: string | null
    lastName?: string | null
    phone: string | null
    email: string | null
  } | null
  nickname: string | null
  avatarUrl: string | null
  phone?: string | null
  gender?: string | null
  dateOfBirth?: string | null
  language?: string | null
  /** When this gym approved them, not when the account was made. */
  memberSince?: string | null
  isGuest?: boolean
  /** Added by the gym at the desk: there is no login, and maybe no email. */
  addedAtDesk?: boolean
  flags?: Record<string, boolean>
  isCheckedIn: boolean
  currentSubscription: ProfileSubscriptionData | null
  /** The renewal bought to follow the current plan. */
  upcomingSubscription?: ProfileSubscriptionData | null
  addons?: ProfileSubscriptionData[]
  previousSubscriptions: ProfileSubscriptionData[]
  totalAscents: number
  children: {
    id: string
    firstName: string
    lastName: string | null
    subscriptionStatus: SubscriptionStatus | null
  }[]
}

interface CheckIn {
  id: string
  checkedInAt: string
  checkedOutAt: string | null
}

interface ChildCheckInGroup {
  childId: string
  childName: string
  checkIns: CheckIn[]
}

interface Props {
  userId: string
  onClose: () => void
  /** A change made here (a freeze) that the page underneath should re-read. */
  onChanged?: () => void
  /**
   * Shown to look at, with every action hidden. A trainer opening someone
   * booked into their class: they may read the profile, but acting on a member
   * stays with the people assigned to them, and the Worker refuses it anyway.
   */
  readOnly?: boolean
}

const CHECK_INS_PAGE_SIZE = 20

function mergeChildCheckIns(
  existing: ChildCheckInGroup[],
  incoming: ChildCheckInGroup[],
): ChildCheckInGroup[] {
  const result = [...existing]
  for (const group of incoming) {
    const idx = result.findIndex((g) => g.childId === group.childId)
    if (idx >= 0) {
      result[idx] = { ...result[idx], checkIns: [...result[idx].checkIns, ...group.checkIns] }
    } else {
      result.push(group)
    }
  }
  return result
}

export function MemberProfileModal({ userId, onClose, onChanged, readOnly = false }: Props) {
  const { getAccessTokenSilently } = useAuth()
  const { t } = useTranslation()
  const { primaryColor } = useGymThemeContext()
  const apiUrl = import.meta.env.VITE_API_URL as string

  const [data, setData] = useState<MemberDetails | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [activeTab, setActiveTab] = useState('overview')
  const [childTarget, setChildTarget] = useState<string | null>(null)
  const details = usePersonDetailItems()
  const [editing, setEditing] = useState(false)

  // Correcting a member's details needs the gym-wide `members` permission. A
  // trainer can open this profile for their own members but the endpoint
  // refuses them, so they are not shown a pencil that only leads to a 403.
  const role = useRole()
  const staffPermissions = useStaffPermissions()
  const canEdit = !readOnly && (role === 'gym_owner' || staffPermissions?.members === true)
  // Freezing changes what the member paid for, so it sits with whoever manages
  // subscriptions. The endpoint refuses a trainer regardless.
  const canFreeze = !readOnly && (role === 'gym_owner' || staffPermissions?.subscriptions === true)
  const [freezeMode, setFreezeMode] = useState<'freeze' | 'unfreeze' | null>(null)

  const [checkIns, setCheckIns] = useState<CheckIn[] | null>(null)
  const [childCheckIns, setChildCheckIns] = useState<ChildCheckInGroup[]>([])
  const [checkInsLoading, setCheckInsLoading] = useState(false)
  const [checkInsError, setCheckInsError] = useState(false)
  const [checkInsHasMore, setCheckInsHasMore] = useState(false)
  const [checkInsLoadingMore, setCheckInsLoadingMore] = useState(false)

  const checkInsOffsetRef = useRef(0)
  const checkInsScrollRef = useRef<HTMLDivElement | null>(null)
  const childrenScrollRef = useRef<HTMLDivElement | null>(null)

  const fetchCheckInsPage = useCallback(
    async (offsetVal: number, append: boolean) => {
      if (!append) {
        setCheckInsLoading(true)
        setCheckInsError(false)
      } else {
        setCheckInsLoadingMore(true)
      }
      try {
        const token = await getAccessTokenSilently()
        const res = await fetch(
          `${apiUrl}/members/${encodeURIComponent(userId)}/check-ins?limit=${CHECK_INS_PAGE_SIZE}&offset=${offsetVal}`,
          { headers: { Authorization: `Bearer ${token}` } },
        )
        if (res.ok) {
          const body = (await res.json()) as {
            checkIns: CheckIn[]
            childCheckIns?: ChildCheckInGroup[]
            has_more: boolean
          }
          if (append) {
            setCheckIns((prev) => [...(prev ?? []), ...body.checkIns])
            setChildCheckIns((prev) => mergeChildCheckIns(prev, body.childCheckIns ?? []))
          } else {
            setCheckIns(body.checkIns)
            setChildCheckIns(body.childCheckIns ?? [])
          }
          setCheckInsHasMore(body.has_more)
          checkInsOffsetRef.current = offsetVal
        } else {
          if (!append) setCheckInsError(true)
        }
      } catch {
        if (!append) setCheckInsError(true)
      } finally {
        if (!append) setCheckInsLoading(false)
        else setCheckInsLoadingMore(false)
      }
    },
    [getAccessTokenSilently, apiUrl, userId],
  )

  const loadMoreCheckIns = useCallback(() => {
    if (checkInsLoadingMore || !checkInsHasMore) return
    void fetchCheckInsPage(checkInsOffsetRef.current + CHECK_INS_PAGE_SIZE, true)
  }, [checkInsLoadingMore, checkInsHasMore, fetchCheckInsPage])

  const checkInsSentinelRef = useInfiniteScroll(
    loadMoreCheckIns,
    checkInsHasMore,
    checkInsScrollRef,
  )
  const childrenSentinelRef = useInfiniteScroll(
    loadMoreCheckIns,
    checkInsHasMore,
    childrenScrollRef,
  )

  // `quiet` re-reads after an edit without swapping the whole profile for a spinner.
  const loadDetails = useCallback(
    async (quiet = false) => {
      if (!quiet) {
        setLoading(true)
        setError(false)
      }
      try {
        const token = await getAccessTokenSilently()
        const res = await fetch(`${apiUrl}/members/${encodeURIComponent(userId)}/details`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (res.ok) {
          setData((await res.json()) as MemberDetails)
        } else if (!quiet) {
          setError(true)
        }
      } catch {
        if (!quiet) setError(true)
      } finally {
        if (!quiet) setLoading(false)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userId],
  )

  useEffect(() => {
    void loadDetails()
  }, [loadDetails])

  useEffect(() => {
    if ((activeTab !== 'checkIns' && activeTab !== 'childrenCheckIns') || checkIns !== null) return
    void fetchCheckInsPage(0, false)
    // checkIns intentionally omitted: the null check is the "has already loaded" guard
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, userId, fetchCheckInsPage])

  return (
    <GymDialog onClose={onClose} size="2xl">
      <Card className="flex min-h-0 flex-col shadow-none">
        <CardHeader className="flex shrink-0 items-start justify-between gap-3 pb-2">
          {loading ? (
            <div className="flex w-full justify-center py-4">
              <Spinner size="sm" color="primary" />
            </div>
          ) : error || !data ? (
            <p className="text-sm text-danger">{t('common.errorDefault')}</p>
          ) : (
            <div className="flex items-center gap-4">
              <MemberAvatar
                userId={data.userId}
                name={data.name}
                avatarExt={null}
                avatarUpdatedAt={null}
                avatarUrl={data.avatarUrl}
                size="lg"
                primaryColor={primaryColor}
              />
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <MemberName
                    name={data.name}
                    nickname={data.nickname}
                    className="text-lg font-semibold"
                  />
                  {data.flags?.is_club_member && <ClubMemberStar />}
                </div>
                {data.email && (
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="truncate text-sm text-foreground/50">{data.email}</p>
                    {/* Nobody is behind a desk member's address to prove it. */}
                    {!data.addedAtDesk && (
                      <Chip
                        size="sm"
                        variant="flat"
                        color={data.emailVerifiedAt ? 'success' : 'default'}
                      >
                        {data.emailVerifiedAt
                          ? t('members.emailVerified')
                          : t('members.emailNotVerified')}
                      </Chip>
                    )}
                  </div>
                )}
                {data.guardian && (
                  <p className="text-sm text-foreground/70">
                    {t('members.guardianContact', { name: data.guardian.name })}
                    {data.guardian.phone && (
                      <>
                        {' · '}
                        <a className="text-primary underline" href={`tel:${data.guardian.phone}`}>
                          {data.guardian.phone}
                        </a>
                      </>
                    )}
                    {data.guardian.email && (
                      <>
                        {' · '}
                        <a
                          className="text-primary underline"
                          href={`mailto:${data.guardian.email}`}
                        >
                          {data.guardian.email}
                        </a>
                      </>
                    )}
                  </p>
                )}
                <div className="mt-1 flex flex-wrap gap-1">
                  {data.isCheckedIn && (
                    <span className="rounded-full bg-success/10 px-2 py-0.5 text-xs font-medium text-success">
                      {t('members.inGymBadge')}
                    </span>
                  )}
                  <SubBadge status={data.currentSubscription?.status} />
                  {data.isGuest && (
                    <Chip size="sm" variant="flat" color="warning">
                      {t('members.guestBadge')}
                    </Chip>
                  )}
                  {data.addedAtDesk && (
                    <Chip size="sm" variant="flat">
                      {t('members.deskBadge')}
                    </Chip>
                  )}
                  {/* Booked by phone with a name and number only: the desk
                      finishes the record when they come in. */}
                  {data.addedAtDesk && !data.dateOfBirth && (
                    <Chip
                      size="sm"
                      variant="flat"
                      color="warning"
                      title={t('members.detailsMissingHint')}
                    >
                      {t('members.detailsMissing')}
                    </Chip>
                  )}
                </div>
                {data.addedAtDesk && !data.dateOfBirth && canEdit && (
                  <p className="mt-1 text-xs text-foreground/60">
                    {t('members.detailsMissingHint')}
                  </p>
                )}
              </div>
            </div>
          )}
          <Button isIconOnly size="sm" variant="light" onClick={onClose} aria-label="Close">
            <svg
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </Button>
        </CardHeader>

        {data && !loading && (
          <CardBody className="overflow-hidden pt-0">
            <Tabs
              aria-label={t('members.profileTabsLabel')}
              selectedKey={activeTab}
              onSelectionChange={(key) => setActiveTab(key as string)}
            >
              <Tab key="overview" title={t('members.tabOverview')}>
                <div className="flex flex-col gap-4 overflow-y-auto max-h-[55vh]">
                  <ProfileDetails
                    onEdit={canEdit ? () => setEditing(true) : undefined}
                    items={[
                      { label: t('members.detailsPhone'), value: data.phone },
                      details.birth(data.dateOfBirth),
                      details.gender(data.gender),
                      {
                        label: t('members.detailsMemberSince'),
                        value: data.memberSince ? formatDetailDate(data.memberSince) : null,
                      },
                      details.language(data.language),
                    ]}
                  />

                  {/* Current subscription */}
                  <div>
                    <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-foreground/40">
                      {t('members.currentSubscription')}
                    </p>
                    {data.currentSubscription ? (
                      <CurrentSubscriptionCard
                        sub={data.currentSubscription}
                        onFreeze={canFreeze ? () => setFreezeMode('freeze') : undefined}
                        onUnfreeze={canFreeze ? () => setFreezeMode('unfreeze') : undefined}
                      />
                    ) : (
                      <p className="text-sm text-foreground/30">{t('members.noSubscription')}</p>
                    )}
                    <div className="mt-2">
                      <RenewalSection
                        current={data.currentSubscription}
                        upcoming={data.upcomingSubscription ?? null}
                        canRenew={canFreeze}
                        memberEmail={data.email}
                        memberUserId={userId}
                        memberName={data.name}
                        onRenewed={() => {
                          void loadDetails(true)
                          onChanged?.()
                        }}
                      />
                    </div>
                    {freezeMode && data.currentSubscription && (
                      <FreezeSubscriptionModal
                        sub={data.currentSubscription}
                        mode={freezeMode}
                        onClose={() => setFreezeMode(null)}
                        onDone={() => {
                          setFreezeMode(null)
                          void loadDetails(true)
                          onChanged?.()
                        }}
                      />
                    )}
                  </div>

                  <AddonsSection addons={data.addons ?? []} readOnly={readOnly} />

                  {/* Total ascents */}
                  <div className="flex items-center justify-between rounded-lg bg-content2/50 px-3 py-2">
                    <p className="text-sm text-foreground/60">
                      {t('members.totalAscents', { count: data.totalAscents })}
                    </p>
                    <span className="text-sm font-semibold">{data.totalAscents}</span>
                  </div>

                  {/* Trainers */}
                  <TrainerAssignmentsSection
                    memberUserId={userId}
                    memberEmail={data.email}
                    memberName={data.name}
                  />

                  {/* Children */}
                  <div>
                    <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-foreground/40">
                      {t('members.childrens')}
                    </p>
                    {data.children.length === 0 ? (
                      <p className="text-sm text-foreground/30">
                        {t('members.noChildrenRegistered')}
                      </p>
                    ) : (
                      <div className="flex flex-col gap-1.5">
                        {data.children.map((child) => {
                          const childFullName = [child.firstName, child.lastName]
                            .filter(Boolean)
                            .join(' ')
                          return (
                            <button
                              type="button"
                              key={child.id}
                              onClick={() => setChildTarget(child.id)}
                              title={t('members.viewProfile')}
                              className="flex w-full items-center justify-between rounded-lg bg-content2/50 px-3 py-2 text-left transition-colors hover:bg-default/10"
                            >
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-foreground/30">└</span>
                                <p className="text-sm">{childFullName}</p>
                                <span className="shrink-0 rounded-full bg-primary/10 px-1.5 py-0.5 text-xs text-primary">
                                  {t('members.childBadge')}
                                </span>
                              </div>
                              <SubBadge status={child.subscriptionStatus ?? undefined} />
                            </button>
                          )
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </Tab>

              <Tab key="checkIns" title={t('members.tabCheckIns')}>
                <div ref={checkInsScrollRef} className="overflow-y-auto max-h-[55vh]">
                  {checkInsLoading && (
                    <div className="flex justify-center py-8">
                      <Spinner size="sm" color="primary" />
                    </div>
                  )}
                  {checkInsError && (
                    <p className="text-sm text-danger">{t('common.errorDefault')}</p>
                  )}
                  {!checkInsLoading && !checkInsError && checkIns !== null && (
                    <>
                      {checkIns.length === 0 ? (
                        <p className="text-sm text-foreground/30">{t('members.noCheckIns')}</p>
                      ) : (
                        <>
                          <div className="mb-2 grid grid-cols-[1fr_auto_auto_auto] gap-3 px-3">
                            <p className="text-xs font-semibold uppercase tracking-wide text-foreground/40">
                              {t('members.checkInDate')}
                            </p>
                            <p className="text-xs font-semibold uppercase tracking-wide text-foreground/40">
                              {t('members.checkInTime')}
                            </p>
                            <p className="text-xs font-semibold uppercase tracking-wide text-foreground/40">
                              {t('members.checkOutTime')}
                            </p>
                            <p className="text-xs font-semibold uppercase tracking-wide text-foreground/40">
                              {t('members.duration')}
                            </p>
                          </div>
                          <div className="flex flex-col gap-1">
                            {checkIns.map((ci) => {
                              const dateLabel = new Date(ci.checkedInAt).toLocaleDateString(
                                undefined,
                                { year: 'numeric', month: 'short', day: 'numeric' },
                              )
                              const timeIn = new Date(ci.checkedInAt).toLocaleTimeString(
                                undefined,
                                { hour: '2-digit', minute: '2-digit' },
                              )
                              const timeOut = ci.checkedOutAt
                                ? new Date(ci.checkedOutAt).toLocaleTimeString(undefined, {
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  })
                                : '—'
                              const duration = formatDuration(
                                ci.checkedInAt,
                                ci.checkedOutAt,
                                t('members.checkInActive'),
                              )
                              return (
                                <div
                                  key={ci.id}
                                  className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-3 rounded-lg bg-content2/30 px-3 py-2"
                                >
                                  <p className="text-sm text-foreground/70">{dateLabel}</p>
                                  <p className="text-sm tabular-nums text-foreground/60">
                                    {timeIn}
                                  </p>
                                  <p className="text-sm tabular-nums text-foreground/60">
                                    {timeOut}
                                  </p>
                                  <span className="rounded-full bg-content3/50 px-2 py-0.5 text-xs font-medium text-foreground/60">
                                    {duration}
                                  </span>
                                </div>
                              )
                            })}
                          </div>
                          {checkInsLoadingMore && (
                            <div className="flex justify-center py-3">
                              <Spinner size="sm" color="primary" />
                            </div>
                          )}
                          <div ref={checkInsSentinelRef} />
                        </>
                      )}
                    </>
                  )}
                </div>
              </Tab>

              {data.children.length > 0 && (
                <Tab key="childrenCheckIns" title={t('members.tabChildrenCheckIns')}>
                  <div ref={childrenScrollRef} className="overflow-y-auto max-h-[55vh]">
                    {checkInsLoading && (
                      <div className="flex justify-center py-8">
                        <Spinner size="sm" color="primary" />
                      </div>
                    )}
                    {checkInsError && (
                      <p className="text-sm text-danger">{t('common.errorDefault')}</p>
                    )}
                    {!checkInsLoading && !checkInsError && checkIns !== null && (
                      <>
                        {childCheckIns.length === 0 ? (
                          <p className="text-sm text-foreground/30">{t('members.noCheckIns')}</p>
                        ) : (
                          <>
                            <div className="flex flex-col gap-4">
                              {childCheckIns.map((group) => (
                                <div key={group.childId}>
                                  <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-foreground/40">
                                    {group.childName}
                                  </p>
                                  <div className="mb-2 grid grid-cols-[1fr_auto_auto_auto] gap-3 px-3">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-foreground/40">
                                      {t('members.checkInDate')}
                                    </p>
                                    <p className="text-xs font-semibold uppercase tracking-wide text-foreground/40">
                                      {t('members.checkInTime')}
                                    </p>
                                    <p className="text-xs font-semibold uppercase tracking-wide text-foreground/40">
                                      {t('members.checkOutTime')}
                                    </p>
                                    <p className="text-xs font-semibold uppercase tracking-wide text-foreground/40">
                                      {t('members.duration')}
                                    </p>
                                  </div>
                                  <div className="flex flex-col gap-1">
                                    {group.checkIns.map((ci) => {
                                      const dateLabel = new Date(ci.checkedInAt).toLocaleDateString(
                                        undefined,
                                        { year: 'numeric', month: 'short', day: 'numeric' },
                                      )
                                      const timeIn = new Date(ci.checkedInAt).toLocaleTimeString(
                                        undefined,
                                        { hour: '2-digit', minute: '2-digit' },
                                      )
                                      const timeOut = ci.checkedOutAt
                                        ? new Date(ci.checkedOutAt).toLocaleTimeString(undefined, {
                                            hour: '2-digit',
                                            minute: '2-digit',
                                          })
                                        : '—'
                                      const duration = formatDuration(
                                        ci.checkedInAt,
                                        ci.checkedOutAt,
                                        t('members.checkInActive'),
                                      )
                                      return (
                                        <div
                                          key={ci.id}
                                          className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-3 rounded-lg bg-content2/30 px-3 py-2"
                                        >
                                          <p className="text-sm text-foreground/70">{dateLabel}</p>
                                          <p className="text-sm tabular-nums text-foreground/60">
                                            {timeIn}
                                          </p>
                                          <p className="text-sm tabular-nums text-foreground/60">
                                            {timeOut}
                                          </p>
                                          <span className="rounded-full bg-content3/50 px-2 py-0.5 text-xs font-medium text-foreground/60">
                                            {duration}
                                          </span>
                                        </div>
                                      )
                                    })}
                                  </div>
                                </div>
                              ))}
                            </div>
                            {checkInsLoadingMore && (
                              <div className="flex justify-center py-3">
                                <Spinner size="sm" color="primary" />
                              </div>
                            )}
                            <div ref={childrenSentinelRef} />
                          </>
                        )}
                      </>
                    )}
                  </div>
                </Tab>
              )}

              <Tab key="subscriptions" title={t('members.tabSubscriptions')}>
                <div className="overflow-y-auto max-h-[55vh]">
                  {data.previousSubscriptions.length === 0 ? (
                    <p className="text-sm text-foreground/30">
                      {t('members.noPreviousSubscriptions')}
                    </p>
                  ) : (
                    <div className="flex flex-col gap-1">
                      {data.previousSubscriptions.map((sub) => (
                        <PreviousSubscriptionRow key={sub.id} sub={sub} />
                      ))}
                    </div>
                  )}
                </div>
              </Tab>

              {/* Mounted only while selected, so the list is fetched on first open. */}
              <Tab key="documents" title={t('members.tabDocuments')}>
                <SignedDocumentsTab userId={userId} />
              </Tab>
            </Tabs>
          </CardBody>
        )}
      </Card>
      {/* A member the gym added has nobody behind the profile to keep it
          current, so the gym may correct all of it: the email and the parent
          too. The form is the one that added them. */}
      {editing && data?.addedAtDesk && (
        <AddMemberModal
          existing={{
            userId,
            firstName: data.firstName ?? null,
            lastName: data.lastName ?? null,
            dateOfBirth: data.dateOfBirth ?? null,
            gender: data.gender ?? null,
            // A minor's contact is the parent's, and lives with the parent.
            phone: data.guardian ? data.guardian.phone : (data.phone ?? null),
            email: data.guardian ? data.guardian.email : data.email || null,
            guardianFirstName: data.guardian?.firstName ?? null,
            guardianLastName: data.guardian?.lastName ?? null,
          }}
          onClose={() => setEditing(false)}
          onAdded={() => {
            setEditing(false)
            void loadDetails(true)
          }}
        />
      )}

      {editing && data && !data.addedAtDesk && (
        <EditMemberDetailsModal
          userId={userId}
          details={{
            firstName: data.firstName ?? null,
            lastName: data.lastName ?? null,
            phone: data.phone ?? null,
            gender: data.gender ?? null,
            dateOfBirth: data.dateOfBirth ?? null,
          }}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false)
            void loadDetails(true)
          }}
        />
      )}
      {/* The parent card in the child's modal leads back here, so it just closes. */}
      {childTarget && (
        <ChildProfileModal
          childId={childTarget}
          readOnly={readOnly}
          onClose={() => setChildTarget(null)}
          onOpenParent={() => setChildTarget(null)}
          // The child's badge on this profile and the page below both change.
          onChanged={() => {
            void loadDetails(true)
            onChanged?.()
          }}
        />
      )}
    </GymDialog>
  )
}
